/**
 * Smart Slot Matching Algorithm
 * AI-powered track matching based on audio features and criteria
 */

import type { SlotCriteria } from '../stores/workbench';
import type { TrackWithFeatures } from '../api/types';
import { toCamelot, getCompatibleKeys, getKeyDistance } from './camelot';

/**
 * Match result with score
 */
export interface MatchResult {
  track: TrackWithFeatures;
  score: number;
  breakdown: ScoreBreakdown;
}

/**
 * Score breakdown for transparency
 */
export interface ScoreBreakdown {
  bpm: number;
  key: number;
  energy: number;
  valence: number;
  danceability: number;
  total: number;
}

/**
 * Check if track passes hard constraints
 */
export function passesHardConstraints(
  track: TrackWithFeatures,
  criteria: SlotCriteria
): boolean {
  const { features } = track;
  if (!features) return false;

  // BPM constraint (hard)
  if (criteria.bpm) {
    if (features.tempo < criteria.bpm.min || features.tempo > criteria.bpm.max) {
      return false;
    }
  }

  // Energy constraint (hard)
  if (criteria.energy) {
    if (features.energy < criteria.energy.min || features.energy > criteria.energy.max) {
      return false;
    }
  }

  // Key constraint (hard if specified)
  if (criteria.keys && criteria.keys.length > 0 && features.key !== undefined) {
    const trackKey = toCamelot(features.key, features.mode);

    // Check if track key is in allowed list OR is compatible with any allowed key
    const isDirectMatch = criteria.keys.includes(trackKey);
    const compatibleKeys = criteria.keys.flatMap(k => getCompatibleKeys(k));
    const isCompatible = compatibleKeys.includes(trackKey);

    if (!isDirectMatch && !isCompatible) {
      return false;
    }
  }

  return true;
}

/**
 * Calculate match score (0-100)
 */
export function calculateMatchScore(
  track: TrackWithFeatures,
  criteria: SlotCriteria
): number {
  const { features } = track;
  if (!features) return 0;

  const breakdown = calculateScoreBreakdown(track, criteria);
  return breakdown.total;
}

/**
 * Calculate detailed score breakdown
 */
export function calculateScoreBreakdown(
  track: TrackWithFeatures,
  criteria: SlotCriteria
): ScoreBreakdown {
  const { features } = track;

  const breakdown: ScoreBreakdown = {
    bpm: 0,
    key: 0,
    energy: 0,
    valence: 0,
    danceability: 0,
    total: 0,
  };

  if (!features) return breakdown;

  // BPM Score (0-30 points)
  if (criteria.bpm) {
    const center = (criteria.bpm.min + criteria.bpm.max) / 2;
    const range = criteria.bpm.max - criteria.bpm.min;

    // Perfect center = 30 points, edges of range = 20 points
    const distance = Math.abs(features.tempo - center);
    const normalizedDistance = distance / (range / 2);
    breakdown.bpm = Math.max(0, 30 - normalizedDistance * 10);
  } else {
    // No BPM criteria = neutral score
    breakdown.bpm = 15;
  }

  // Key Compatibility Score (0-40 points)
  if (criteria.keys && criteria.keys.length > 0 && features.key !== undefined) {
    const trackKey = toCamelot(features.key, features.mode);

    // Exact match in criteria
    if (criteria.keys.includes(trackKey)) {
      breakdown.key = 40;
    } else {
      // Find best compatibility with any allowed key
      let bestDistance = Infinity;
      for (const allowedKey of criteria.keys) {
        const distance = getKeyDistance(trackKey, allowedKey);
        if (distance < bestDistance) {
          bestDistance = distance;
        }
      }

      // Score based on distance: 0=40, 1=25, 2=15, 3=5, 4+=0
      if (bestDistance === 0) breakdown.key = 40;
      else if (bestDistance === 1) breakdown.key = 25;
      else if (bestDistance === 2) breakdown.key = 15;
      else if (bestDistance === 3) breakdown.key = 5;
      else breakdown.key = 0;
    }
  } else {
    // No key criteria = neutral score
    breakdown.key = 20;
  }

  // Energy Score (0-15 points)
  if (criteria.energy) {
    const center = (criteria.energy.min + criteria.energy.max) / 2;
    const distance = Math.abs(features.energy - center);
    // Perfect center = 15, max edges = 10
    breakdown.energy = Math.max(10, 15 - distance * 10);
  } else {
    breakdown.energy = 12;
  }

  // Valence Score (0-10 points)
  if (criteria.valence) {
    const center = (criteria.valence.min + criteria.valence.max) / 2;
    const distance = Math.abs(features.valence - center);
    // Perfect center = 10, edges = 5
    breakdown.valence = Math.max(5, 10 - distance * 10);
  } else {
    breakdown.valence = 7;
  }

  // Danceability Score (0-5 points)
  if (criteria.danceability) {
    const center = (criteria.danceability.min + criteria.danceability.max) / 2;
    const distance = Math.abs(features.danceability - center);
    // Perfect center = 5, edges = 2
    breakdown.danceability = Math.max(2, 5 - distance * 6);
  } else {
    breakdown.danceability = 3;
  }

  // Total score
  breakdown.total = Math.round(
    breakdown.bpm +
    breakdown.key +
    breakdown.energy +
    breakdown.valence +
    breakdown.danceability
  );

  return breakdown;
}

/**
 * Find matching tracks and return top matches sorted by score
 */
export function findMatches(
  candidates: TrackWithFeatures[],
  criteria: SlotCriteria,
  limit: number = 10
): MatchResult[] {
  // Filter by hard constraints
  const passing = candidates.filter(track => passesHardConstraints(track, criteria));

  // Score remaining tracks
  const scored = passing.map(track => ({
    track,
    score: calculateMatchScore(track, criteria),
    breakdown: calculateScoreBreakdown(track, criteria),
  }));

  // Sort by score (descending) and return top N
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Format score for display
 */
export function formatScore(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Very Good';
  if (score >= 60) return 'Good';
  if (score >= 45) return 'Fair';
  return 'Poor';
}

/**
 * Get score color class
 */
export function getScoreColor(score: number): string {
  if (score >= 90) return 'text-emerald-400';
  if (score >= 75) return 'text-green-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 45) return 'text-orange-400';
  return 'text-red-400';
}
